import { apiClient, type ApiResponse } from "./apiClient";
import {
  mockDeleteAutoTaggingPlan,
  mockFetchAutoTaggingPlans,
  mockFetchConfigurableConcepts,
  mockFetchDefaultPrompt,
  mockPatchAutoTaggingPlan,
  mockPatchAutoTaggingPlanActive,
  mockTriggerAutoTaggingBundleTask,
} from "./autoTaggingMockData";

/** Flip to true only while the Regulations List dialog uses local mock data. */
export const USE_AUTO_TAGGING_MOCK = false;

export const AI_PLAN_DEFAULT_OBJECT_TYPE = "Insight";
export const AI_PLAN_DEFAULT_OBJECT_TYPE_CODE = "insight";
export const AI_PLAN_DEFAULT_SUB_TYPE_CODE = "regulation";

/** Named type/sub-type option from per-tenant feature flags (`code` for API, `name` for UI). */
export interface AutoTaggingPlanNamedOption {
  code: string;
  name: string;
}

export interface AutoTaggingPlanTypeOption {
  code: string;
  name: string;
  objectSubTypes: AutoTaggingPlanNamedOption[];
  /** From `/auto-tagging/plans/types`. Tagging / Enhanced UIs only list `true`. */
  configurable?: boolean;
}

/** Normalize UI/API type values to snake_case API codes (e.g. "Insight" → "insight"). */
export function formatPlanTypeLabelToCode(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join("_");
}

/** @deprecated Prefer API `name`; kept as legacy fallback when name is missing. */
export function formatPlanTypeCode(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return "";

  return trimmed
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Pass through backend type/sub-type codes as-is (trim only).
 * Do not slugify spaces to underscores or force lowercase.
 */
export function normalizePlanTypeApiCode(value: string): string {
  return value.trim();
}

function uniqueNormalizedCodes(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const value of values) {
    const code = normalizePlanTypeApiCode(value);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/**
 * Resolve `target_type` values for dimension/concept APIs.
 * Object Type wins; if only Sub-Type is selected, map it back to parent type(s).
 */
export function resolveConceptQueryTargetTypes(options: {
  objectType?: string | null;
  objectSubType?: string | null;
  planTypes: readonly AutoTaggingPlanTypeOption[];
  fallbackTypeCodes?: readonly string[];
}): string[] {
  const objectType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (objectType) return [objectType];

  const objectSubType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (objectSubType) {
    const matched: string[] = [];
    const seen = new Set<string>();
    for (const planType of options.planTypes) {
      const typeCode = normalizePlanTypeApiCode(planType.code);
      if (!typeCode || seen.has(typeCode)) continue;
      const belongsToType = planType.objectSubTypes.some(
        (item) => normalizePlanTypeApiCode(item.code) === objectSubType,
      );
      if (!belongsToType) continue;
      seen.add(typeCode);
      matched.push(typeCode);
    }
    if (matched.length > 0) return matched;
  }

  return uniqueNormalizedCodes(options.fallbackTypeCodes ?? []);
}

/**
 * `kind` for GET `/auto-tagging/plans/configurable-concepts`.
 * Omitted → backend default `impacted`.
 */
export type ConfigurableConceptsKind =
  | "impacted"
  | "reference"
  | "assessment_record";

/** Query params for configurable-concepts / output-concepts. */
export function buildConceptQueryParams(
  targetType: string,
  subType?: string | null,
  kind?: ConfigurableConceptsKind | null,
): { target_type: string; sub_type?: string; kind?: ConfigurableConceptsKind } | null {
  const target_type = normalizePlanTypeApiCode(targetType);
  if (!target_type) return null;
  const sub_type = normalizePlanTypeApiCode(subType ?? "");
  const kindCode = (kind ?? "").trim() as ConfigurableConceptsKind | "";
  const params: {
    target_type: string;
    sub_type?: string;
    kind?: ConfigurableConceptsKind;
  } = { target_type };
  if (sub_type) params.sub_type = sub_type;
  if (
    kindCode === "impacted" ||
    kindCode === "reference" ||
    kindCode === "assessment_record"
  ) {
    params.kind = kindCode;
  }
  return params;
}

function normalizeNamedTypeOption(raw: unknown): AutoTaggingPlanNamedOption | null {
  if (typeof raw === "string") {
    const code = normalizePlanTypeApiCode(raw);
    if (!code) return null;
    return { code, name: formatPlanTypeCode(code) };
  }

  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = normalizePlanTypeApiCode(
    String(record.code ?? record.value ?? "").trim(),
  );
  if (!code) return null;

  const name = String(record.name ?? record.label ?? "").trim() || formatPlanTypeCode(code);
  return { code, name };
}

function normalizeSubTypes(raw: unknown): AutoTaggingPlanNamedOption[] {
  if (!Array.isArray(raw)) return [];

  const subTypes: AutoTaggingPlanNamedOption[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const option = normalizeNamedTypeOption(item);
    if (!option || seen.has(option.code)) continue;
    seen.add(option.code);
    subTypes.push(option);
  }

  return subTypes;
}

function readConfigurableFlag(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "false" || normalized === "0") return false;
    if (normalized === "true" || normalized === "1") return true;
  }
  return Boolean(value);
}

/** Tagging Plan / Enhanced Tagging Rule (and their result lists) hide non-configurable types. */
export function filterConfigurablePlanTypes(
  planTypes: readonly AutoTaggingPlanTypeOption[],
): AutoTaggingPlanTypeOption[] {
  return planTypes.filter((item) => item.configurable !== false);
}

function normalizePlanTypeItem(raw: unknown): AutoTaggingPlanTypeOption | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = normalizePlanTypeApiCode(
    String(
      record.code ??
        record.object_type ??
        record.objectType ??
        record.type ??
        "",
    ).trim(),
  );
  if (!code) return null;

  const name =
    String(record.name ?? record.label ?? "").trim() || formatPlanTypeCode(code);
  const objectSubTypes = normalizeSubTypes(
    record.sub_types ??
      record.subTypes ??
      record.object_sub_types ??
      record.objectSubTypes,
  );

  return {
    code,
    name,
    objectSubTypes,
    configurable: readConfigurableFlag(record.configurable),
  };
}

function extractPlanTypesPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["types", "items", "object_types", "objectTypes"] as const;

  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }

  const nested = record.data;
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === "object") {
    const nestedRecord = nested as Record<string, unknown>;
    for (const key of nestedKeys) {
      if (Array.isArray(nestedRecord[key])) return nestedRecord[key] as unknown[];
    }
  }

  return [];
}

export function normalizeAutoTaggingPlanTypes(
  payload: unknown,
): AutoTaggingPlanTypeOption[] {
  return extractPlanTypesPayload(payload)
    .map(normalizePlanTypeItem)
    .filter((item): item is AutoTaggingPlanTypeOption => item !== null);
}

export function resolveDefaultObjectType(
  planTypes: AutoTaggingPlanTypeOption[],
): string {
  const matched = planTypes.find(
    (item) => item.code === AI_PLAN_DEFAULT_OBJECT_TYPE_CODE,
  );
  return matched?.code ?? planTypes[0]?.code ?? "";
}

export function getSubTypesForObjectType(
  planTypes: AutoTaggingPlanTypeOption[],
  objectType: string,
): AutoTaggingPlanNamedOption[] {
  const code = normalizePlanTypeApiCode(objectType);
  return planTypes.find((item) => item.code === code)?.objectSubTypes ?? [];
}

export function resolvePlanTypeDisplayName(
  planTypes: AutoTaggingPlanTypeOption[],
  typeCode: string,
): string {
  const code = normalizePlanTypeApiCode(typeCode);
  if (!code) return "";
  return planTypes.find((item) => item.code === code)?.name ?? typeCode.trim();
}

export function resolvePlanSubTypeDisplayName(
  planTypes: AutoTaggingPlanTypeOption[],
  typeCode: string,
  subTypeCode: string,
): string {
  const code = normalizePlanTypeApiCode(subTypeCode);
  if (!code) return "";
  const subTypes = getSubTypesForObjectType(planTypes, typeCode);
  return subTypes.find((item) => item.code === code)?.name ?? subTypeCode.trim();
}

export function buildDefaultPlanItemSelection(
  planTypes: AutoTaggingPlanTypeOption[],
): { objectType: string; objectSubType: string } {
  const objectType = resolveDefaultObjectType(planTypes);
  const objectSubTypes = getSubTypesForObjectType(planTypes, objectType);
  const preferredSubType = objectSubTypes.find(
    (item) => item.code === AI_PLAN_DEFAULT_SUB_TYPE_CODE,
  );
  return {
    objectType,
    objectSubType: preferredSubType?.code ?? objectSubTypes[0]?.code ?? "",
  };
}

let planTypesInflight: Promise<AutoTaggingPlanTypeOption[]> | null = null;

/**
 * Loads `/auto-tagging/plans/types`. Concurrent callers share one in-flight
 * request; the promise is cleared when it settles so Add/Edit remounts refetch.
 */
export async function fetchAutoTaggingPlanTypes(): Promise<
  AutoTaggingPlanTypeOption[]
> {
  if (planTypesInflight) return planTypesInflight;

  planTypesInflight = (async () => {
    const result = await apiClient.get<unknown>("/auto-tagging/plans/types");
    if (result.status !== "success") {
      return [];
    }
    return normalizeAutoTaggingPlanTypes(result.data);
  })().finally(() => {
    planTypesInflight = null;
  });

  return planTypesInflight;
}

/** @internal Test helper — clears the in-flight plan-types request. */
export function __resetAutoTaggingPlanTypesCacheForTests(): void {
  planTypesInflight = null;
}

export function normalizeAutoTaggingEnabled(payload: unknown): boolean {
  if (typeof payload === "boolean") return payload;
  if (!payload || typeof payload !== "object") return false;
  const record = payload as Record<string, unknown>;
  return record.enabled === true;
}

export async function fetchAutoTaggingEnabled(): Promise<boolean> {
  try {
    const result = await apiClient.get<unknown>("/auto-tagging/enabled");
    if (result.status !== "success") {
      return false;
    }
    return normalizeAutoTaggingEnabled(result.data);
  } catch (error) {
    console.error("Failed to fetch auto-tagging enabled status:", error);
    return false;
  }
}

export async function updateAutoTaggingEnabled(
  enabled: boolean,
): Promise<boolean> {
  const result = await apiClient.put<unknown>("/auto-tagging/enabled", {
    enabled,
  });
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${enabled ? "enable" : "disable"} auto-tagging.`;
    throw new Error(message);
  }

  if (result.data !== undefined && result.data !== null) {
    return normalizeAutoTaggingEnabled(result.data);
  }
  return enabled;
}

const CONCEPT_CODE_TO_LABEL: Record<string, string> = {
  ProductType: "Product Type",
  IngredientName: "Ingredient",
  Ingredient: "Ingredient",
  TherapeuticArea: "Therapeutic Area",
  HealthAuthority: "Health Authority",
  Department: "Department",
};

export function formatConceptCodeToLabel(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return "";
  return CONCEPT_CODE_TO_LABEL[trimmed] ?? trimmed;
}

export interface ConfigurableConcept {
  /** Machine identifier used on write paths (API `name`). */
  code: string;
  /** Machine name from the API (`name`). */
  name: string;
  /** KM display label — render this; never derive from `name`. */
  display_name: string;
}

/** Prefer API `display_name`; soft-fallback only for mocks / legacy payloads. */
export function getConceptDisplayLabel(
  concept: Pick<ConfigurableConcept, "code" | "name" | "display_name">,
): string {
  const display = concept.display_name?.trim();
  if (display) return display;
  return concept.name?.trim() || concept.code?.trim() || "";
}

export interface ConceptValue {
  code: string;
  name: string;
  description?: string;
}

export interface AutoTaggingPlan {
  id: number;
  target_type: string;
  sub_type?: string;
  concept: string;
  concept_display_name?: string;
  all_values: boolean;
  concept_values: ConceptValue[];
  prompt: string;
  active: boolean;
}

export interface AutoTaggingBundleTaskRegulation {
  regulation_id: string;
  version: string;
}

export interface AutoTaggingBundleTaskRequest {
  regulations: AutoTaggingBundleTaskRegulation[];
  plan_ids: number[];
  force_update: boolean;
}

export interface AutoTaggingBundleTaskResponse {
  uuid: string;
  sub_tasks: string[];
  completed_tasks: string[];
  status: string;
  trigger_by: string;
  force_update: boolean;
  total_tasks: number;
  executed_at: string;
  completed_at?: string;
}

export interface PatchAutoTaggingPlanPayload {
  concept?: string;
  all_values?: boolean;
  concept_values?: ConceptValue[];
  prompt?: string;
}

export const AUTO_TAGGING_START_SUCCESS_TOAST =
  "Auto-tagging started successfully. Please check back shortly for results.";
export const AUTO_TAGGING_START_FAILED_TOAST =
  "Auto-tagging started failed. Please check back shortly for results.";
export const AUTO_TAGGING_SAVE_SUCCESS_TOAST = "Item saved successfully.";
export const AUTO_TAGGING_DELETE_SUCCESS_TOAST = "Item delete successfully.";
export const AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST = "item activated successfully";
export const AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST =
  "item deactivated successfully";

/**
 * Normalize concept entries from configurable-concepts / output-concepts.
 * Submit `name` (stored as `code`); render `display_name`.
 */
function normalizeAiPlanConfigurableConcept(
  raw: unknown,
): ConfigurableConcept | null {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return { code: trimmed, name: trimmed, display_name: trimmed };
  }

  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  // API contract: `name` is the machine identifier write paths validate against.
  const machineName = String(
    record.name ?? record.code ?? record.concept_code ?? record.concept_name ?? "",
  ).trim();
  if (!machineName) return null;
  const displayName = String(record.display_name ?? "").trim() || machineName;
  return {
    code: machineName,
    name: machineName,
    display_name: displayName,
  };
}

function extractConfigurableConceptsPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["concepts", "items", "data"] as const;

  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }

  const nested = record.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>;
    for (const key of nestedKeys) {
      if (Array.isArray(nestedRecord[key])) return nestedRecord[key] as unknown[];
    }
  }

  return [];
}

export function normalizeConfigurableConcepts(
  payload: unknown,
): ConfigurableConcept[] {
  return extractConfigurableConceptsPayload(payload)
    .map(normalizeAiPlanConfigurableConcept)
    .filter((item): item is ConfigurableConcept => item !== null);
}

export function normalizeConfigurableConcept(
  raw: unknown,
): ConfigurableConcept | null {
  return normalizeAiPlanConfigurableConcept(raw);
}

export function parseConfigurableConceptsPayload(
  payload: unknown,
): ConfigurableConcept[] {
  return normalizeConfigurableConcepts(payload);
}

const configurableConceptsInflight = new Map<
  string,
  Promise<ConfigurableConcept[]>
>();
const outputConceptsInflight = new Map<string, Promise<ConfigurableConcept[]>>();

export async function fetchConfigurableConcepts(
  targetType: string,
  subType?: string | null,
  kind?: ConfigurableConceptsKind | null,
): Promise<ConfigurableConcept[]> {
  if (USE_AUTO_TAGGING_MOCK) {
    return mockFetchConfigurableConcepts();
  }
  const params = buildConceptQueryParams(targetType, subType, kind);
  if (!params) {
    return [];
  }

  const cacheKey = [
    params.target_type,
    params.sub_type ?? "",
    params.kind ?? "",
  ].join("|");

  const existing = configurableConceptsInflight.get(cacheKey);
  if (existing) return existing;

  const request = (async () => {
    const result = await apiClient.get<unknown>(
      "/auto-tagging/plans/configurable-concepts",
      params,
    );
    if (result.status !== "success") {
      return [];
    }
    return parseConfigurableConceptsPayload(result.data);
  })().finally(() => {
    configurableConceptsInflight.delete(cacheKey);
  });

  configurableConceptsInflight.set(cacheKey, request);
  return request;
}

/** Concepts available as Then-step output tags for conditional (enhanced) plans. */
export async function fetchConditionalPlanOutputConcepts(
  targetType: string,
  subType?: string | null,
): Promise<ConfigurableConcept[]> {
  const params = buildConceptQueryParams(targetType, subType);
  if (!params) {
    return [];
  }

  const cacheKey = params.sub_type
    ? `${params.target_type}|${params.sub_type}`
    : params.target_type;

  const existing = outputConceptsInflight.get(cacheKey);
  if (existing) return existing;

  const request = (async () => {
    const result = await apiClient.get<unknown>(
      "/auto-tagging/conditional-plans/output-concepts",
      params,
    );
    if (result.status !== "success") {
      return [];
    }
    return parseConfigurableConceptsPayload(result.data);
  })().finally(() => {
    outputConceptsInflight.delete(cacheKey);
  });

  outputConceptsInflight.set(cacheKey, request);
  return request;
}

export type ConditionalPlanCreateRequest = {
  /** Assessment Name — required on POST; optional on PATCH. */
  name?: string;
  target_type: string;
  /**
   * Reference Dimension filters. Same write shape as Classification
   * `reference_items`. Dimension value codes are not sent as `sub_type`.
   */
  reference_items: AutoTaggingPlanDimensionItemWrite[];
  condition: {
    combinator: string;
    rules: unknown[];
    not: boolean;
  };
  concept: string;
  concept_value: {
    name: string;
    code: string;
    description: string;
  };
};

/** Create a single enhanced tagging rule (conditional plan). */
export async function createConditionalPlan(
  payload: ConditionalPlanCreateRequest,
): Promise<ApiResponse<unknown>> {
  const result = await apiClient.post<unknown>(
    "/auto-tagging/conditional-plans",
    payload,
  );
  if (result.status !== "success") {
    const err = new Error(
      formatConditionalPlanMutationError(result, "create"),
    ) as Error & { responseData?: unknown };
    err.responseData = result;
    throw err;
  }
  invalidateAutoTaggingPlanNamesCache();
  return result;
}

/** Update an existing enhanced tagging rule (conditional plan). */
export async function updateConditionalPlan(
  id: string,
  payload: ConditionalPlanCreateRequest,
): Promise<ApiResponse<unknown>> {
  const trimmedId = id.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
    payload,
  );
  if (result.status !== "success") {
    const err = new Error(
      formatConditionalPlanMutationError(result, "update"),
    ) as Error & { responseData?: unknown };
    err.responseData = result;
    throw err;
  }
  invalidateAutoTaggingPlanNamesCache();
  return result;
}

function readExistingPlanIdFromMutationPayload(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const record = payload as Record<string, unknown>;
  const errors = record.errors;
  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (!item || typeof item !== "object") continue;
      const details = (item as { details?: unknown }).details;
      if (!details || typeof details !== "object") continue;
      const id = String(
        (details as Record<string, unknown>).existing_plan_id ??
          (details as Record<string, unknown>).existingPlanId ??
          "",
      ).trim();
      if (id) return id;
    }
  }
  return String(
    record.existing_plan_id ?? record.existingPlanId ?? record.id ?? "",
  ).trim();
}

function formatConditionalPlanMutationError(
  result: ApiResponse<unknown>,
  action: "create" | "update",
): string {
  const fallback =
    action === "create"
      ? "Failed to create Conditional Classification."
      : "Failed to update Conditional Classification.";

  const payload = result.data ?? result;
  let fromErrors: string | null = null;
  if (payload && typeof payload === "object") {
    const errors = (payload as { errors?: unknown }).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const messages = errors
        .map((item) =>
          item && typeof item === "object"
            ? String((item as { message?: unknown }).message ?? "").trim()
            : "",
        )
        .filter(Boolean);
      if (messages.length > 0) fromErrors = messages.join("\n");
    }
  }

  const message = fromErrors || result.message?.trim() || fallback;
  const conflictId = readExistingPlanIdFromMutationPayload(payload);
  if (!conflictId) return message;
  if (
    messageMentionsAnyRuleId(message) ||
    messageContainsToken(message, conflictId)
  ) {
    return message;
  }
  return `${message} (Rule ${conflictId})`;
}

function isRuleIdChar(ch: string | undefined): boolean {
  if (!ch) return false;
  const code = ch.charCodeAt(0);
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    ch === "_" ||
    ch === "-"
  );
}

function isWhitespaceChar(ch: string | undefined): boolean {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r";
}

/** Case-insensitive whole-token match (alphanumeric / _ / - boundaries). */
function messageContainsToken(message: string, token: string): boolean {
  const haystack = message.toLowerCase();
  const needle = token.toLowerCase();
  if (!needle) return false;

  let start = 0;
  while (start <= haystack.length - needle.length) {
    const idx = haystack.indexOf(needle, start);
    if (idx === -1) return false;
    const before = idx === 0 ? undefined : haystack[idx - 1];
    const after =
      idx + needle.length >= haystack.length
        ? undefined
        : haystack[idx + needle.length];
    if (!isRuleIdChar(before) && !isRuleIdChar(after)) return true;
    start = idx + 1;
  }
  return false;
}

/** True when the message already embeds a "Rule …" id label. */
function messageMentionsAnyRuleId(message: string): boolean {
  const lower = message.toLowerCase();
  let searchFrom = 0;
  while (searchFrom < lower.length) {
    const idx = lower.indexOf("rule", searchFrom);
    if (idx === -1) return false;
    let i = idx + 4;
    while (isWhitespaceChar(message[i])) i += 1;
    if (message[i] === "#") {
      i += 1;
      while (isWhitespaceChar(message[i])) i += 1;
    }
    if (isRuleIdChar(message[i])) return true;
    searchFrom = idx + 4;
  }
  return false;
}

/** Toggle active on a conditional plan via PATCH (partial update). */
export async function updateConditionalPlanActive(
  planId: string,
  active: boolean,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
    { active },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${active ? "activate" : "deactivate"} Conditional Classification.`;
    throw new Error(message);
  }

  return result;
}

/** Delete a conditional plan (Enhanced Tagging Rule). */
export async function deleteConditionalPlan(
  planId: string,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.delete<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to delete Conditional Classification.";
    throw new Error(message);
  }

  invalidateAutoTaggingPlanNamesCache();
  return result;
}

export const CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE = 10;

export interface ConditionalPlanRecord {
  id: string;
  /** Assessment Name — API `name`. */
  name: string;
  target_type: string;
  /** Legacy object subtype. New writes keep Reference filters in `reference_items`. */
  sub_type: string;
  concept: string;
  concept_value: AutoTaggingPlanSubmitConceptValue;
  active: boolean;
  /** Raw condition payload from the API (RQB-compatible group). */
  condition: unknown;
  /** Reference Dimension filters (Classification `reference_items` shape). */
  reference_items: AutoTaggingPlanDimensionItem[];
}

export interface FetchConditionalPlansParams {
  target_type?: string;
  sub_type?: string;
  concept?: string;
  concept_value_code?: string;
  /** Top-bar search → fuzzy Assessment Name match. */
  keyword?: string;
  /** Assessment Name filter → exact match. */
  name?: string;
  page?: number;
  page_size?: number;
}

export interface FetchConditionalPlansResult {
  items: ConditionalPlanRecord[];
  total: number;
  page: number;
  pageSize: number;
}

function normalizeConditionalPlanConceptValue(
  raw: unknown,
): AutoTaggingPlanSubmitConceptValue {
  if (!raw || typeof raw !== "object") {
    return { name: "", code: "", description: "" };
  }
  const record = raw as Record<string, unknown>;
  return {
    name: String(record.name ?? "").trim(),
    code: String(record.code ?? "").trim(),
    description: String(record.description ?? "").trim(),
  };
}

function normalizeConditionalPlanRecord(
  raw: unknown,
  index: number,
): ConditionalPlanRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;

  const concept = String(
    record.concept ?? record.concept_code ?? record.conceptCode ?? "",
  ).trim();
  const conceptValue = normalizeConditionalPlanConceptValue(
    record.concept_value ?? record.conceptValue,
  );
  const id = String(
    record.id ?? record.plan_id ?? record.planId ?? record.uuid ?? index,
  ).trim();
  const targetType = normalizePlanTypeApiCode(
    String(
      record.target_type ??
        record.targetType ??
        record.object_type ??
        record.objectType ??
        "",
    ),
  );
  const subType = normalizePlanTypeApiCode(
    String(
      record.sub_type ??
        record.subType ??
        record.object_sub_type ??
        record.objectSubType ??
        "",
    ),
  );

  if (!id && !concept && !conceptValue.code) return null;

  const name = String(record.name ?? record.assessment_name ?? "").trim();

  return {
    id: id || `conditional-plan-${index}`,
    name,
    target_type: targetType,
    sub_type: subType,
    concept,
    concept_value: conceptValue,
    active: record.active !== false && record.enabled !== false,
    condition: record.condition ?? null,
    reference_items: normalizeDimensionItemsList(
      record.reference_items ?? record.referenceItems,
    ),
  };
}

function extractConditionalPlansListPayload(payload: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }
  if (!payload || typeof payload !== "object") {
    return { items: [] };
  }

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["plans", "items", "results", "data"] as const;

  let items: unknown[] = [];
  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) {
      items = record[key] as unknown[];
      break;
    }
  }

  const nested =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : undefined;

  if (items.length === 0 && nested) {
    for (const key of nestedKeys) {
      if (Array.isArray(nested[key])) {
        items = nested[key] as unknown[];
        break;
      }
    }
  }

  const total =
    readPayloadNumber(record, "total", "total_count", "totalCount") ??
    (nested
      ? readPayloadNumber(nested, "total", "total_count", "totalCount")
      : undefined);
  const page =
    readPayloadNumber(record, "page") ??
    (nested ? readPayloadNumber(nested, "page") : undefined);
  const pageSize =
    readPayloadNumber(record, "page_size", "pageSize") ??
    (nested ? readPayloadNumber(nested, "page_size", "pageSize") : undefined);

  return { items, total, page, pageSize };
}

function readPayloadNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

export function normalizeConditionalPlans(
  payload: unknown,
): ConditionalPlanRecord[] {
  return extractConditionalPlansListPayload(payload)
    .items.map(normalizeConditionalPlanRecord)
    .filter((item): item is ConditionalPlanRecord => item !== null);
}

export function buildConditionalPlansQuery(options: {
  objectType?: string;
  objectSubType?: string;
  concept?: string;
  conceptValueCode?: string;
  /** Top-bar search → API `keyword` (fuzzy). */
  keyword?: string;
  /** Assessment Name filter → API `name` (exact). */
  name?: string;
  page?: number;
  pageSize?: number;
}): FetchConditionalPlansParams {
  const params: FetchConditionalPlansParams = {
    page: options.page ?? 1,
    page_size: options.pageSize ?? CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE,
  };
  // UI Object Type / Object Sub-Type → API `target_type` / `sub_type`.
  const targetType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (targetType) params.target_type = targetType;
  const subType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (subType) params.sub_type = subType;
  const concept = options.concept?.trim();
  if (concept) params.concept = concept;
  const valueCode = options.conceptValueCode?.trim();
  if (valueCode) params.concept_value_code = valueCode;
  const keyword = normalizeAutoTaggingPlanNameQuery(options.keyword ?? "");
  if (keyword) params.keyword = keyword;
  const name = normalizeAutoTaggingPlanNameQuery(options.name ?? "");
  if (name) params.name = name;
  return params;
}

/** List conditional plans (enhanced tagging rules), newest first. */
export async function fetchConditionalPlans(
  params: FetchConditionalPlansParams = {},
): Promise<FetchConditionalPlansResult> {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE;
  const query: Record<string, string | number> = {
    page,
    page_size: pageSize,
  };
  if (params.target_type?.trim()) query.target_type = params.target_type.trim();
  if (params.sub_type?.trim()) query.sub_type = params.sub_type.trim();
  if (params.concept?.trim()) query.concept = params.concept.trim();
  if (params.concept_value_code?.trim()) {
    query.concept_value_code = params.concept_value_code.trim();
  }
  if (params.keyword?.trim()) query.keyword = params.keyword.trim();
  if (params.name?.trim()) query.name = params.name.trim();

  try {
    const result = await apiClient.get<unknown>(
      "/auto-tagging/conditional-plans",
      query,
    );
    if (result.status !== "success") {
      return { items: [], total: 0, page, pageSize };
    }

    const extracted = extractConditionalPlansListPayload(result.data);
    const items = extracted.items
      .map(normalizeConditionalPlanRecord)
      .filter((item): item is ConditionalPlanRecord => item !== null);

    return {
      items,
      total: extracted.total ?? items.length,
      page: extracted.page ?? page,
      pageSize: extracted.pageSize ?? pageSize,
    };
  } catch (error) {
    console.error("Failed to fetch conditional plans:", error);
    return { items: [], total: 0, page, pageSize };
  }
}

export function mapConditionalPlanToRuleRow(
  plan: ConditionalPlanRecord,
  options?: {
    conditionDescription?: string;
    planTypes?: AutoTaggingPlanTypeOption[];
    concepts?: ConfigurableConcept[];
  },
): {
  id: string;
  name: string;
  objectType: string;
  objectSubType: string;
  objectTypeName: string;
  objectSubTypeName: string;
  dimension: string;
  value: string;
  conditionDescription: string;
  enabled: boolean;
  conceptCode: string;
  conceptValueCode: string;
  conceptValueName: string;
  condition: unknown;
} {
  const planTypes = options?.planTypes ?? [];
  const concepts = options?.concepts ?? [];
  const conceptMeta = concepts.find(
    (item) => item.code === plan.concept || item.name === plan.concept,
  );
  const valueLabel =
    plan.concept_value.name.trim() || plan.concept_value.code.trim();
  return {
    id: plan.id,
    name: plan.name,
    objectType: plan.target_type,
    objectSubType: plan.sub_type,
    objectTypeName: resolvePlanTypeDisplayName(planTypes, plan.target_type),
    objectSubTypeName: resolvePlanSubTypeDisplayName(
      planTypes,
      plan.target_type,
      plan.sub_type,
    ),
    dimension: conceptMeta
      ? getConceptDisplayLabel(conceptMeta)
      : plan.concept,
    value: valueLabel,
    conditionDescription: options?.conditionDescription ?? "",
    enabled: plan.active,
    conceptCode: plan.concept,
    conceptValueCode: plan.concept_value.code,
    conceptValueName: plan.concept_value.name.trim() || plan.concept_value.code,
    condition: plan.condition,
  };
}

const defaultPromptInflight = new Map<string, Promise<string>>();

function normalizeDefaultPromptPayload(payload: unknown): string {
  if (typeof payload === "string") return payload.trim();
  if (!payload || typeof payload !== "object") return "";

  const record = payload as Record<string, unknown>;
  return String(
    record.prompt ??
      record.default_prompt ??
      record.defaultPrompt ??
      record.assessment_prompt ??
      record.assessmentPrompt ??
      "",
  ).trim();
}

function buildDefaultPromptCacheKey(
  conceptCode: string,
  targetType: string,
  subType: string,
  assessmentType: string,
): string {
  return `${targetType}|${subType}|${assessmentType}|${conceptCode}`;
}

/**
 * Normalize Assessment Template → API `assessment_type` for default-prompt.
 * Accepts UI labels (`Tagging` / `Implementation`) or API codes.
 * Empty / unknown → omit (backend returns Tagging default).
 */
export function normalizeDefaultPromptAssessmentType(
  assessmentType: string | undefined,
): string {
  const normalized = (assessmentType ?? "").trim().toLowerCase();
  if (normalized === "implementation") return "implementation";
  if (normalized === "tagging") return "tagging";
  return "";
}

export type FetchDefaultAutoTaggingPlanPromptOptions = {
  /** Reference Object type → API `target_type`. */
  targetType?: string;
  /**
   * Optional explicit API `sub_type`. Callers must not pass Reference Dimension
   * value codes here — Classification Plan derives subtype from Object only
   * (insight → regulation when omitted).
   */
  subType?: string;
  /**
   * Assessment Template → API `assessment_type` (`tagging` | `implementation`).
   * UI labels are accepted. Omitted when empty — backend returns Tagging text.
   */
  assessmentType?: string;
};

export async function fetchDefaultAutoTaggingPlanPrompt(
  conceptCode: string,
  options?: FetchDefaultAutoTaggingPlanPromptOptions,
): Promise<string> {
  const normalizedConceptCode = conceptCode.trim();
  if (!normalizedConceptCode) return "";

  const targetType =
    normalizePlanTypeApiCode(options?.targetType ?? "") ||
    AI_PLAN_DEFAULT_OBJECT_TYPE_CODE;
  // Prefer explicit subtype when provided; otherwise Object-scoped default
  // (insight → regulation). Never treat Reference Dimension values as sub_type.
  const subType =
    normalizePlanTypeApiCode(options?.subType ?? "") ||
    resolveObjectScopedPlanSubType(targetType);
  const assessmentType = normalizeDefaultPromptAssessmentType(
    options?.assessmentType,
  );

  const cacheKey = buildDefaultPromptCacheKey(
    normalizedConceptCode,
    targetType,
    subType,
    assessmentType,
  );

  const inflight = defaultPromptInflight.get(cacheKey);
  if (inflight) return inflight;

  const request = apiClient
    .get<unknown>("/auto-tagging/plans/default-prompt", {
      concept: normalizedConceptCode,
      target_type: targetType,
      ...(subType ? { sub_type: subType } : {}),
      ...(assessmentType ? { assessment_type: assessmentType } : {}),
    })
    .then((result) => {
      if (result.status !== "success") {
        throw new Error(
          result.message?.trim() || "Failed to load default assessment prompt.",
        );
      }

      // Non-(insight, regulation) types return an empty string (not 422).
      return normalizeDefaultPromptPayload(result.data);
    })
    .finally(() => {
      defaultPromptInflight.delete(cacheKey);
    });

  defaultPromptInflight.set(cacheKey, request);
  return request;
}

export function __resetDefaultPromptCacheForTests(): void {
  defaultPromptInflight.clear();
}

/** Historical Assessment Name values for the freeSolo dropdown. */
export function normalizeAutoTaggingPlanNamesPayload(payload: unknown): string[] {
  if (payload == null) return [];

  let namesRaw: unknown = payload;
  if (typeof payload === "object" && !Array.isArray(payload)) {
    const record = payload as Record<string, unknown>;
    namesRaw =
      record.names ??
      record.name_list ??
      record.nameList ??
      record.data ??
      payload;
    if (
      namesRaw &&
      typeof namesRaw === "object" &&
      !Array.isArray(namesRaw) &&
      "names" in (namesRaw as object)
    ) {
      namesRaw = (namesRaw as { names: unknown }).names;
    }
  }

  if (!Array.isArray(namesRaw)) return [];

  const seen = new Set<string>();
  const names: string[] = [];
  for (const item of namesRaw) {
    const value =
      typeof item === "string"
        ? item.trim()
        : item && typeof item === "object"
          ? String(
              (item as Record<string, unknown>).name ??
                (item as Record<string, unknown>).value ??
                "",
            ).trim()
          : "";
    if (!value || seen.has(value)) continue;
    seen.add(value);
    names.push(value);
  }
  return names;
}

/** Session cache + in-flight share for GET `/plans/names`. */
let autoTaggingPlanNamesCache: string[] | null = null;
let autoTaggingPlanNamesInflight: Promise<string[]> | null = null;

/** Drop cached Assessment Names so the next read refetches (after create/edit/delete). */
export function invalidateAutoTaggingPlanNamesCache(): void {
  autoTaggingPlanNamesCache = null;
}

/** @internal Test helper — clears names cache and in-flight request. */
export function __resetAutoTaggingPlanNamesCacheForTests(): void {
  autoTaggingPlanNamesCache = null;
  autoTaggingPlanNamesInflight = null;
}

/**
 * GET `/plans/names` — shared Assessment Name options (all plan tabs).
 * Concurrent callers (StrictMode / tab remount) share one request; results are
 * cached for the session until {@link invalidateAutoTaggingPlanNamesCache}.
 * Pass `{ force: true }` (e.g. dropdown open) to bypass the session cache.
 */
export async function fetchAutoTaggingPlanNames(options?: {
  force?: boolean;
}): Promise<string[]> {
  if (!options?.force && autoTaggingPlanNamesCache) {
    return autoTaggingPlanNamesCache;
  }
  if (autoTaggingPlanNamesInflight) {
    return autoTaggingPlanNamesInflight;
  }

  autoTaggingPlanNamesInflight = (async () => {
    const result = await apiClient.get<unknown>("/plans/names");
    if (result.status !== "success") {
      throw new Error(
        result.message?.trim() || "Failed to load assessment names.",
      );
    }
    const names = normalizeAutoTaggingPlanNamesPayload(result.data ?? result);
    autoTaggingPlanNamesCache = names;
    return names;
  })().finally(() => {
    autoTaggingPlanNamesInflight = null;
  });

  return autoTaggingPlanNamesInflight;
}

export interface AutoTaggingPlanSubmitConceptValue {
  name: string;
  code: string;
  description: string;
}

/** Dimension filter value on write/read (`reference_items` / `assessment_record_items`). */
export type AutoTaggingPlanDimensionValue = {
  name: string;
  code: string;
  description: string;
};

/**
 * Dimension item write shape (create / update / CSV).
 * Rules: `all_values=true` → `values` must be `[]`; `all_values=false` → `values` non-empty;
 * concepts must be unique within an array.
 */
export type AutoTaggingPlanDimensionItemWrite = {
  concept: string;
  all_values: boolean;
  values: AutoTaggingPlanDimensionValue[];
};

/** Dimension item read shape — includes optional display label. */
export type AutoTaggingPlanDimensionItem = AutoTaggingPlanDimensionItemWrite & {
  display_name?: string;
};

/** Reference Dimension concept for Doc Type filters on Classification plans. */
export const CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT = "DocType";

/** Only supported `tagging_approach` value in the current API version. */
export const CLASSIFICATION_TAGGING_APPROACH_INSIGHT_ONLY = "insight_only";

export interface AutoTaggingPlanRecord {
  id: string;
  target_type: string;
  sub_type: string;
  concept: string;
  all_values: boolean;
  concept_values: AutoTaggingPlanSubmitConceptValue[];
  active: boolean;
  prompt: string;
  /** Assessment Name — API `name`. */
  name: string;
  /** Assessment Template — API `assessment_type` (e.g. tagging, implementation). */
  assessment_type: string;
  /** Execution layer (int ≥ 0). */
  layer: number;
  /** Reference object filters (Doc Type etc.). */
  reference_items: AutoTaggingPlanDimensionItem[];
  /** Impact=Y write-back dimensions (Update Assessment Record). */
  assessment_record_items: AutoTaggingPlanDimensionItem[];
  /** Tagging approach — this version supports `insight_only` only. */
  tagging_approach: string;
}

export interface FetchAutoTaggingPlansParams {
  target_type?: string;
  sub_type?: string;
  concept?: string;
  /** Top-bar search → fuzzy Assessment Name match. */
  keyword?: string;
  /** Assessment Name filter → exact match. */
  name?: string;
  /** Assessment Template exact match (e.g. tagging, implementation). */
  assessment_type?: string;
  page?: number;
  page_size?: number;
}

/**
 * Default Assessment Template API code when the form does not supply one.
 * Prefer mapping from Assessment Template UI (`Tagging` → `tagging`).
 */
export const TAGGING_PLAN_ASSESSMENT_TYPE = "tagging";

function normalizeDimensionValue(
  raw: unknown,
): AutoTaggingPlanDimensionValue | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = String(record.code ?? "").trim();
  if (!code) return null;
  const name = String(record.name ?? "").trim();
  return {
    name: name || code,
    code,
    description: String(record.description ?? "").trim(),
  };
}

/**
 * Normalize a dimension item from API/CSV payloads.
 * Invalid items (empty concept, or all_values/values rule violations) are dropped.
 */
export function normalizeAutoTaggingPlanDimensionItem(
  raw: unknown,
): AutoTaggingPlanDimensionItem | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const concept = String(record.concept ?? "").trim();
  if (!concept) return null;

  const allValues = Boolean(record.all_values ?? record.allValues);
  const rawValues = record.values ?? record.concept_values ?? record.conceptValues;
  const values = Array.isArray(rawValues)
    ? rawValues
        .map(normalizeDimensionValue)
        .filter((item): item is AutoTaggingPlanDimensionValue => item !== null)
    : [];

  if (allValues) {
    const displayName = String(record.display_name ?? record.displayName ?? "").trim();
    return {
      concept,
      ...(displayName ? { display_name: displayName } : {}),
      all_values: true,
      values: [],
    };
  }

  if (values.length === 0) return null;
  const displayName = String(record.display_name ?? record.displayName ?? "").trim();
  return {
    concept,
    ...(displayName ? { display_name: displayName } : {}),
    all_values: false,
    values,
  };
}

function normalizeDimensionItemsList(raw: unknown): AutoTaggingPlanDimensionItem[] {
  if (!Array.isArray(raw)) return [];
  const items: AutoTaggingPlanDimensionItem[] = [];
  const seenConcepts = new Set<string>();
  for (const entry of raw) {
    const item = normalizeAutoTaggingPlanDimensionItem(entry);
    if (!item) continue;
    const key = item.concept.toLowerCase();
    if (seenConcepts.has(key)) continue;
    seenConcepts.add(key);
    items.push(item);
  }
  return items;
}

/** Build a write dimension item; enforces all_values ↔ values rules. */
export function buildAutoTaggingPlanDimensionItem(input: {
  concept: string;
  allValues: boolean;
  values?: Array<{ code: string; name?: string; description?: string }>;
}): AutoTaggingPlanDimensionItemWrite | null {
  const concept = input.concept.trim();
  if (!concept) return null;

  if (input.allValues) {
    return { concept, all_values: true, values: [] };
  }

  const values: AutoTaggingPlanDimensionValue[] = [];
  const seen = new Set<string>();
  for (const value of input.values ?? []) {
    const code = String(value.code ?? "").trim();
    if (!code) continue;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const name = String(value.name ?? "").trim();
    values.push({
      name: name || code,
      code,
      description: String(value.description ?? "").trim(),
    });
  }
  if (values.length === 0) return null;
  return { concept, all_values: false, values };
}

/** Strip read-only `display_name` before create/update. */
export function toAutoTaggingPlanDimensionItemWrite(
  item: AutoTaggingPlanDimensionItem,
): AutoTaggingPlanDimensionItemWrite {
  return {
    concept: item.concept,
    all_values: item.all_values,
    values: item.all_values
      ? []
      : item.values.map((value) => ({
          name: value.name.trim() || value.code,
          code: value.code,
          description: String(value.description ?? "").trim(),
        })),
  };
}

/** Normalize UI name / keyword query for plan list APIs. */
export function normalizeAutoTaggingPlanNameQuery(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export const AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE = 10;

function normalizeConceptValueForList(
  raw: unknown,
): AutoTaggingPlanSubmitConceptValue | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = String(record.code ?? "").trim();
  if (!code) return null;
  return {
    code,
    name: String(record.name ?? ""),
    description: String(record.description ?? ""),
  };
}

function normalizeAutoTaggingPlanRecord(
  raw: unknown,
  index: number,
): AutoTaggingPlanRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;

  const targetType = String(
    record.target_type ?? record.targetType ?? record.object_type ?? record.objectType ?? "",
  ).trim();
  const subType = String(
    record.sub_type ?? record.subType ?? record.object_sub_type ?? record.objectSubType ?? "",
  ).trim();
  const concept = String(record.concept ?? record.concept_code ?? record.conceptCode ?? "").trim();
  const prompt = String(
    record.prompt ?? record.assessment_prompt ?? record.assessmentPrompt ?? record.description ?? "",
  ).trim();
  const name = String(
    record.name ?? record.assessment_name ?? record.assessmentName ?? "",
  ).trim();
  const assessmentType = String(
    record.assessment_type ?? record.assessmentType ?? "",
  )
    .trim()
    .toLowerCase();

  if (!targetType && !subType && !concept && !prompt && !name) return null;

  const rawValues = record.concept_values ?? record.conceptValues ?? record.values;
  const conceptValues = Array.isArray(rawValues)
    ? rawValues
        .map(normalizeConceptValueForList)
        .filter((item): item is AutoTaggingPlanSubmitConceptValue => item !== null)
    : [];

  const id = String(record.id ?? record.plan_id ?? record.planId ?? record.uuid ?? index).trim();

  const layerRaw = Number(record.layer ?? 0);
  const layer = Number.isFinite(layerRaw) && layerRaw >= 0 ? Math.floor(layerRaw) : 0;

  return {
    id: id || `plan-${index}`,
    target_type: targetType,
    sub_type: subType,
    concept,
    all_values: Boolean(record.all_values ?? record.allValues),
    concept_values: conceptValues,
    active: record.active !== false && record.enabled !== false,
    prompt,
    name,
    assessment_type: assessmentType,
    layer,
    reference_items: normalizeDimensionItemsList(
      record.reference_items ?? record.referenceItems,
    ),
    assessment_record_items: normalizeDimensionItemsList(
      record.assessment_record_items ?? record.assessmentRecordItems,
    ),
    tagging_approach: String(
      record.tagging_approach ?? record.taggingApproach ?? "",
    ).trim(),
  };
}

function extractPlansListPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["plans", "items", "results", "data"] as const;

  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }

  const nested = record.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>;
    for (const key of nestedKeys) {
      if (Array.isArray(nestedRecord[key])) return nestedRecord[key] as unknown[];
    }
  }

  return [];
}

function extractAutoTaggingPlansPage(payload: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }
  if (!payload || typeof payload !== "object") {
    return { items: [] };
  }

  const record = payload as Record<string, unknown>;
  const items = extractPlansListPayload(payload);
  const nested =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : undefined;

  const total =
    readPayloadNumber(record, "total", "total_count", "totalCount") ??
    (nested
      ? readPayloadNumber(nested, "total", "total_count", "totalCount")
      : undefined);
  const page =
    readPayloadNumber(record, "page") ??
    (nested ? readPayloadNumber(nested, "page") : undefined);
  const pageSize =
    readPayloadNumber(record, "page_size", "pageSize") ??
    (nested ? readPayloadNumber(nested, "page_size", "pageSize") : undefined);

  return { items, total, page, pageSize };
}

export function normalizeAutoTaggingPlans(payload: unknown): AutoTaggingPlanRecord[] {
  return extractPlansListPayload(payload)
    .map(normalizeAutoTaggingPlanRecord)
    .filter((item): item is AutoTaggingPlanRecord => item !== null);
}

export type FetchAutoTaggingPlansResult = {
  items: AutoTaggingPlanRecord[];
  total: number;
  page: number;
  pageSize: number;
};

export function formatAutoTaggingPlanValues(plan: AutoTaggingPlanRecord): string {
  if (plan.all_values) return "All Values";
  if (plan.concept_values.length === 0) return "";
  return plan.concept_values
    .map((value) => value.name.trim() || value.code)
    .join(", ");
}

export function buildAutoTaggingPlansQuery(options: {
  objectType?: string;
  objectSubType?: string;
  concept?: string;
  /** Top-bar search → API `keyword` (fuzzy). */
  keyword?: string;
  /** Assessment Name filter → API `name` (exact). */
  name?: string;
  /** Assessment Template → API `assessment_type` (exact). */
  assessmentType?: string;
  page?: number;
  pageSize?: number;
}): FetchAutoTaggingPlansParams {
  const params: FetchAutoTaggingPlansParams = {
    page: options.page ?? 1,
    page_size: options.pageSize ?? AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  };

  // UI Object Type / Object Sub-Type → API `target_type` / `sub_type`.
  const targetType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (targetType) {
    params.target_type = targetType;
  }

  const subType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (subType) {
    params.sub_type = subType;
  }

  const concept = options.concept?.trim() ?? "";
  if (concept) {
    params.concept = concept;
  }

  const keyword = normalizeAutoTaggingPlanNameQuery(options.keyword ?? "");
  if (keyword) {
    params.keyword = keyword;
  }

  const name = normalizeAutoTaggingPlanNameQuery(options.name ?? "");
  if (name) {
    params.name = name;
  }

  const assessmentType = (options.assessmentType ?? "").trim().toLowerCase();
  if (assessmentType) {
    params.assessment_type = assessmentType;
  }

  return params;
}

async function fetchAiPlanAutoTaggingPlans(
  params: FetchAutoTaggingPlansParams,
): Promise<FetchAutoTaggingPlansResult> {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE;
  try {
    const result = await apiClient.get<unknown>("/auto-tagging/plans", {
      ...params,
      page,
      page_size: pageSize,
    });
    if (result.status !== "success") {
      return { items: [], total: 0, page, pageSize };
    }

    const extracted = extractAutoTaggingPlansPage(result.data);
    const items = extracted.items
      .map(normalizeAutoTaggingPlanRecord)
      .filter((item): item is AutoTaggingPlanRecord => item !== null);

    return {
      items,
      total: extracted.total ?? items.length,
      page: extracted.page ?? page,
      pageSize: extracted.pageSize ?? pageSize,
    };
  } catch (error) {
    console.error("Failed to fetch auto-tagging plans:", error);
    return { items: [], total: 0, page, pageSize };
  }
}

function toRegulationsListPlan(raw: unknown, index: number): AutoTaggingPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = Number(record.id ?? record.plan_id ?? index);
  if (!Number.isFinite(id)) return null;
  const concept_values = Array.isArray(record.concept_values)
    ? record.concept_values
        .filter((value): value is Record<string, unknown> =>
          Boolean(value && typeof value === "object"),
        )
        .map((value) => ({
          code: String(value.code ?? ""),
          name: String(value.name ?? ""),
          description:
            value.description == null ? undefined : String(value.description),
        }))
    : [];
  return {
    id,
    target_type: String(record.target_type ?? ""),
    sub_type: String(record.sub_type ?? "") || undefined,
    concept: String(record.concept ?? ""),
    concept_display_name:
      typeof record.concept_display_name === "string"
        ? record.concept_display_name
        : undefined,
    all_values: Boolean(record.all_values),
    concept_values,
    prompt: String(record.prompt ?? ""),
    active: record.active !== false,
  };
}

export function fetchAutoTaggingPlans(
  targetType: string | null,
): Promise<AutoTaggingPlan[]>;
export function fetchAutoTaggingPlans(
  params: FetchAutoTaggingPlansParams,
): Promise<FetchAutoTaggingPlansResult>;
export async function fetchAutoTaggingPlans(
  input: string | null | FetchAutoTaggingPlansParams,
): Promise<AutoTaggingPlan[] | FetchAutoTaggingPlansResult> {
  if (typeof input === "object" && input !== null) {
    return fetchAiPlanAutoTaggingPlans(input);
  }
  if (USE_AUTO_TAGGING_MOCK) {
    return mockFetchAutoTaggingPlans(input ?? "");
  }

  const targetType =
    (typeof input === "string" ? input : "").trim() ||
    AI_PLAN_DEFAULT_OBJECT_TYPE_CODE;

  const result = await apiClient.get<unknown>("/auto-tagging/plans", {
    target_type: targetType,
    sub_type: AI_PLAN_DEFAULT_SUB_TYPE_CODE,
  });
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Failed to load auto-tagging plans.");
  }
  return extractPlansListPayload(result.data)
    .map(toRegulationsListPlan)
    .filter((item): item is AutoTaggingPlan => item !== null);
}

/**
 * POST `/auto-tagging/plans` / PATCH `/auto-tagging/plans/{id}` body item.
 * Legacy `sub_type` is no longer sent — Doc Type filters live in `reference_items`.
 */
export interface AutoTaggingPlanSubmitItem {
  name: string | null;
  assessment_type: string;
  target_type: string;
  concept: string;
  all_values: boolean;
  concept_values: AutoTaggingPlanSubmitConceptValue[];
  /** Execution layer (int ≥ 0). Required on create. */
  layer: number;
  /** Reference object filters. Required on create (may be `[]`). */
  reference_items: AutoTaggingPlanDimensionItemWrite[];
  /** Impact=Y write-back. Required on create (may be `[]`). */
  assessment_record_items: AutoTaggingPlanDimensionItemWrite[];
  /**
   * Tagging approach — omitted on write while the UI field is hidden.
   * Still accepted on read for older plans.
   */
  tagging_approach?: string;
  active: boolean;
  /** Empty / unset assessment prompt is sent as null. */
  prompt: string | null;
}

export interface ConceptStatsRequest {
  target_type: string;
  concept: string;
  sub_type: string;
}

export interface ConceptStatsData {
  target_type: string;
  sub_type: string;
  concept: string;
  current_count: number;
  plan_ids: number[];
  limit: number;
  available: number;
  can_create: boolean;
}

/**
 * Object-scoped `sub_type` for concept-stats / default-prompt style APIs.
 * Classification Reference Dimension value codes must never be sent as sub_type
 * — insight → regulation; other object types omit subtype.
 */
export function resolveObjectScopedPlanSubType(objectType: string): string {
  const targetType = normalizePlanTypeApiCode(objectType);
  if (!targetType) return "";
  if (targetType.toLowerCase() === AI_PLAN_DEFAULT_OBJECT_TYPE_CODE) {
    return AI_PLAN_DEFAULT_SUB_TYPE_CODE;
  }
  return "";
}

export function buildConceptComboKey(item: {
  objectType: string;
  /** Kept for callers; Reference Dimension values are ignored for the key. */
  objectSubType: string;
  conceptCode: string;
}): string {
  if (!item.objectType.trim() || !item.conceptCode.trim()) {
    return "";
  }

  return [
    normalizePlanTypeApiCode(item.objectType),
    resolveObjectScopedPlanSubType(item.objectType),
    item.conceptCode.trim(),
  ].join("|");
}

export function countItemsWithSameConceptCombo<
  T extends {
    objectType: string;
    objectSubType: string;
    conceptCode: string;
  },
>(items: readonly T[], target: T): number {
  const key = buildConceptComboKey(target);
  if (!key) return 0;
  return items.filter((item) => buildConceptComboKey(item) === key).length;
}

export function parseConceptStatsData(
  response: ApiResponse<unknown>,
): ConceptStatsData | null {
  if (response.status !== "success" || !response.data) return null;
  if (typeof response.data !== "object") return null;

  const record = response.data as Record<string, unknown>;
  return {
    target_type: String(record.target_type ?? ""),
    sub_type: String(record.sub_type ?? ""),
    concept: String(record.concept ?? ""),
    current_count: Number(record.current_count ?? 0),
    plan_ids: Array.isArray(record.plan_ids)
      ? record.plan_ids.map((id) => Number(id))
      : [],
    limit: Number(record.limit ?? 0),
    available: Number(record.available ?? 0),
    can_create: record.can_create !== false,
  };
}

export function buildConceptStatsHintMessage(
  stats: ConceptStatsData,
  sameComboDraftCount = 1,
): string {
  const remaining = stats.available;

  if (!stats.can_create || remaining <= 0) {
    return `The maximum of ${stats.limit} plans for this combination has been reached (${stats.limit}/${stats.limit} used).`;
  }

  if (sameComboDraftCount > remaining) {
    return `Only ${remaining} plan slot${remaining === 1 ? "" : "s"} remaining for this combination, but this form has ${sameComboDraftCount} item(s) with the same selection.`;
  }

  if (sameComboDraftCount > 1) {
    return `You can add ${remaining} more plan${remaining === 1 ? "" : "s"} for this combination (${stats.current_count}/${stats.limit} used). This form contains ${sameComboDraftCount} item(s) with the same selection.`;
  }

  return `You can add ${remaining} more plan${remaining === 1 ? "" : "s"} for this combination (${stats.current_count}/${stats.limit} used).`;
}

export function buildConceptStatsExceededMessage(
  stats: ConceptStatsData,
  requestedCount: number,
): string {
  if (!stats.can_create || stats.available <= 0) {
    return `The maximum of ${stats.limit} plans for this combination has been reached (${stats.limit}/${stats.limit} used).`;
  }

  return `Only ${stats.available} plan slot${stats.available === 1 ? "" : "s"} remaining for this combination, but you are trying to add ${requestedCount} item(s) with the same selection.`;
}

export function isConceptStatsExceeded(
  stats: ConceptStatsData,
  requestedCount: number,
): boolean {
  return !stats.can_create || requestedCount > stats.available;
}

function mapConceptValueForSubmit(value: {
  code: string;
  name?: string;
  description?: string;
}): AutoTaggingPlanSubmitConceptValue {
  return {
    name: String(value.name ?? ""),
    code: value.code,
    description: String(value.description ?? ""),
  };
}

export function buildAutoTaggingPlanSubmitItem(item: {
  objectType: string;
  /**
   * Kept for concept-stats / UI cascade state. Not sent on create/update —
   * Doc Type filters go through `referenceItems` → `reference_items`.
   */
  objectSubType: string;
  conceptCode: string;
  conceptValues: Array<{
    code: string;
    name?: string;
    description?: string;
  }>;
  assessmentPrompt: string;
  /** Assessment Name → API `name`. */
  assessmentName?: string;
  allValues?: boolean;
  active?: boolean;
  /** Execution layer (int ≥ 0). Defaults to 0. */
  layer?: number;
  /** Reference object filters. Defaults to `[]`. */
  referenceItems?: AutoTaggingPlanDimensionItemWrite[];
  /** Impact=Y write-back dimensions. Defaults to `[]`. */
  assessmentRecordItems?: AutoTaggingPlanDimensionItemWrite[];
  /**
   * Assessment Template API code, or UI label (`Tagging` / `Implementation`).
   * Defaults to `tagging`.
   */
  assessmentType?: string;
}): AutoTaggingPlanSubmitItem {
  const allValues = item.allValues ?? false;
  const prompt = item.assessmentPrompt.trim();
  const name = (item.assessmentName ?? "").trim();

  const layerRaw = item.layer ?? 0;
  const layer =
    Number.isFinite(layerRaw) && layerRaw >= 0 ? Math.floor(layerRaw) : 0;

  const referenceItems = (item.referenceItems ?? []).map((entry) =>
    toAutoTaggingPlanDimensionItemWrite(entry),
  );
  const assessmentRecordItems = (item.assessmentRecordItems ?? []).map((entry) =>
    toAutoTaggingPlanDimensionItemWrite(entry),
  );

  const assessmentTypeRaw = (item.assessmentType ?? "").trim().toLowerCase();
  const assessmentType =
    assessmentTypeRaw === "implementation" ||
    assessmentTypeRaw === "tagging"
      ? assessmentTypeRaw
      : TAGGING_PLAN_ASSESSMENT_TYPE;

  // Do not send `tagging_approach` while the Classification UI field is hidden.
  return {
    name: name || null,
    assessment_type: assessmentType,
    target_type: normalizePlanTypeApiCode(item.objectType),
    concept: item.conceptCode,
    all_values: allValues,
    concept_values: allValues
      ? []
      : item.conceptValues.map(mapConceptValueForSubmit),
    layer,
    reference_items: referenceItems,
    assessment_record_items: assessmentRecordItems,
    active: item.active ?? true,
    prompt: prompt || null,
  };
}

export interface ImportedAssessmentPlanFormItem {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValues: Array<{
    id: string;
    code: string;
    name: string;
    description?: string;
  }>;
  assessmentPrompt: string;
  assessmentName: string;
  assessmentType: string;
  allValues: boolean;
}

export function mapImportedPlanToFormItem(
  plan: AutoTaggingPlanRecord,
): ImportedAssessmentPlanFormItem {
  // Prefer Doc Type codes from reference_items when present (CSV / GET shape).
  const docType = plan.reference_items.find(
    (item) =>
      item.concept.toLowerCase() ===
      CLASSIFICATION_REFERENCE_DOC_TYPE_CONCEPT.toLowerCase(),
  );
  let objectSubType = normalizePlanTypeApiCode(plan.sub_type);
  if (docType) {
    objectSubType = docType.all_values
      ? "__all__"
      : docType.values.map((value) => value.code).join(",");
  }

  return {
    objectType: normalizePlanTypeApiCode(plan.target_type),
    objectSubType,
    conceptCode: plan.concept,
    conceptValues: plan.concept_values.map((value) => ({
      id: value.code,
      code: value.code,
      name: value.name.trim() || value.code,
      description: value.description,
    })),
    assessmentPrompt: plan.prompt,
    assessmentName: plan.name,
    assessmentType: plan.assessment_type,
    allValues: plan.all_values,
  };
}

export type AutoTaggingPlanImportRow = {
  row: number;
  status: string;
  plan: AutoTaggingPlanRecord | null;
  errors: string[];
};

export type AutoTaggingPlanImportResult = {
  rows: AutoTaggingPlanImportRow[];
  total: number;
  validCount: number;
  errorCount: number;
};

function normalizeImportErrorMessage(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    return String(
      record.message ?? record.error ?? record.detail ?? record.code ?? "",
    ).trim();
  }
  return String(raw ?? "").trim();
}

function formatImportRowErrors(rows: AutoTaggingPlanImportRow[]): string {
  const messages = rows
    .filter((item) => item.status !== "valid" || item.errors.length > 0)
    .map((item) => {
      const detail = item.errors.join("; ") || item.status || "invalid";
      // API row index is 0-based; show 1-based for users.
      return `Row ${item.row + 1}: ${detail}`;
    });

  if (messages.length === 0) {
    return "Import validation failed.";
  }

  const preview = messages.slice(0, 3).join(" ");
  if (messages.length <= 3) return preview;
  return `${preview} (+${messages.length - 3} more)`;
}

/** Parse POST /auto-tagging/plans/import `data` shape (`rows` + counts). */
export function normalizeAutoTaggingPlanImportPayload(
  payload: unknown,
): AutoTaggingPlanImportResult {
  if (!payload || typeof payload !== "object") {
    return { rows: [], total: 0, validCount: 0, errorCount: 0 };
  }

  const data = payload as Record<string, unknown>;
  const rawRows = Array.isArray(data.rows) ? data.rows : [];
  const rows: AutoTaggingPlanImportRow[] = rawRows.map((item, index) => {
    if (!item || typeof item !== "object") {
      return {
        row: index,
        status: "error",
        plan: null,
        errors: ["Invalid import row."],
      };
    }

    const record = item as Record<string, unknown>;
    const rowNumber = Number(record.row);
    const row = Number.isFinite(rowNumber) ? rowNumber : index;
    const status = String(record.status ?? "")
      .trim()
      .toLowerCase();
    const plan = normalizeAutoTaggingPlanRecord(record.plan, row);
    const errors = Array.isArray(record.errors)
      ? record.errors.map(normalizeImportErrorMessage).filter(Boolean)
      : [];

    return {
      row,
      status: status || (errors.length > 0 ? "error" : "valid"),
      plan,
      errors,
    };
  });

  const validCountRaw = Number(data.valid_count ?? data.validCount);
  const errorCountRaw = Number(data.error_count ?? data.errorCount);
  const totalRaw = Number(data.total);

  return {
    rows,
    total: Number.isFinite(totalRaw) ? totalRaw : rows.length,
    validCount: Number.isFinite(validCountRaw)
      ? validCountRaw
      : rows.filter((item) => item.status === "valid").length,
    errorCount: Number.isFinite(errorCountRaw)
      ? errorCountRaw
      : rows.filter((item) => item.status !== "valid").length,
  };
}

function extractImportedPlansFromPayload(
  payload: unknown,
): AutoTaggingPlanRecord[] {
  const importResult = normalizeAutoTaggingPlanImportPayload(payload);
  if (importResult.errorCount > 0) {
    throw new Error(formatImportRowErrors(importResult.rows));
  }

  const invalidRows = importResult.rows.filter(
    (item) => item.status !== "valid" || item.errors.length > 0,
  );
  if (invalidRows.length > 0) {
    throw new Error(formatImportRowErrors(invalidRows));
  }

  return importResult.rows
    .map((item) => item.plan)
    .filter((plan): plan is AutoTaggingPlanRecord => plan !== null);
}

export async function importAutoTaggingPlansFromFile(
  file: File,
): Promise<ImportedAssessmentPlanFormItem[]> {
  const formData = new FormData();
  formData.append("file", file);

  const result = await apiClient.post<unknown>(
    "/auto-tagging/plans/import",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to import assessment plan items.";
    throw new Error(message);
  }

  const plans = extractImportedPlansFromPayload(result.data);
  if (plans.length === 0) {
    throw new Error("No items found in the uploaded file.");
  }

  return plans.map(mapImportedPlanToFormItem);
}

export function buildConceptStatsRequest(item: {
  objectType: string;
  /** Kept for callers; Reference Dimension values are not sent as `sub_type`. */
  objectSubType: string;
  conceptCode: string;
}): ConceptStatsRequest {
  return {
    target_type: normalizePlanTypeApiCode(item.objectType),
    concept: item.conceptCode.trim(),
    sub_type: resolveObjectScopedPlanSubType(item.objectType),
  };
}

export function isConceptStatsValidationPassed(
  response: ApiResponse<unknown>,
): boolean {
  if (response.status !== "success") return false;

  const stats = parseConceptStatsData(response);
  if (stats) {
    return stats.can_create && stats.available > 0;
  }

  if (!response.data || typeof response.data !== "object") return true;

  const record = response.data as Record<string, unknown>;
  if (typeof record.can_create === "boolean") return record.can_create;
  if (typeof record.valid === "boolean") return record.valid;
  if (typeof record.is_valid === "boolean") return record.is_valid;
  if (typeof record.passed === "boolean") return record.passed;

  return true;
}

export async function fetchConceptStats(
  params: ConceptStatsRequest,
): Promise<ApiResponse<unknown>> {
  return apiClient.get<unknown>("/auto-tagging/plans/concept-stats", params);
}

export async function validateConceptStatsForItem(item: {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
}): Promise<void> {
  await validateDraftItemsConceptStatsAvailability([item]);
}

export async function validateDraftItemsConceptStatsAvailability(
  items: Array<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
  }>,
): Promise<void> {
  const groups = new Map<
    string,
    Array<{
      objectType: string;
      objectSubType: string;
      conceptCode: string;
    }>
  >();

  items.forEach((item) => {
    const key = buildConceptComboKey(item);
    if (!key) return;
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  });

  for (const groupItems of groups.values()) {
    const statsResponse = await fetchConceptStats(
      buildConceptStatsRequest(groupItems[0]),
    );
    const stats = parseConceptStatsData(statsResponse);
    if (!stats) {
      const message =
        statsResponse.message?.trim() ||
        "Failed to check plan availability.";
      throw new Error(message);
    }

    if (isConceptStatsExceeded(stats, groupItems.length)) {
      throw new Error(
        buildConceptStatsExceededMessage(stats, groupItems.length),
      );
    }
  }
}

export async function submitAutoTaggingPlans(
  plans: AutoTaggingPlanSubmitItem[],
): Promise<ApiResponse<unknown>> {
  const result = await apiClient.post<unknown>("/auto-tagging/plans", {
    plans,
  });
  if (result.status === "success") {
    invalidateAutoTaggingPlanNamesCache();
  }
  return result;
}

export function deleteAutoTaggingPlan(planId: number): Promise<number>;
export function deleteAutoTaggingPlan(
  planId: string,
): Promise<ApiResponse<unknown>>;
export async function deleteAutoTaggingPlan(
  planId: number | string,
): Promise<number | ApiResponse<unknown>> {
  if (typeof planId === "number" && USE_AUTO_TAGGING_MOCK) {
    return mockDeleteAutoTaggingPlan(planId);
  }
  const rawPlanId = String(planId);
  const trimmedId = rawPlanId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.delete<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
  );
  if (result.status !== "success") {
    const message = result.message?.trim() || "Failed to delete Classification.";
    throw new Error(message);
  }

  invalidateAutoTaggingPlanNamesCache();
  return result;
}

export async function updateAutoTaggingPlanActive(
  planId: string,
  active: boolean,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    { active },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${active ? "activate" : "deactivate"} Classification.`;
    throw new Error(message);
  }

  return result;
}

export async function updateAutoTaggingPlan(
  planId: string,
  payload: AutoTaggingPlanSubmitItem,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    payload,
  );
  if (result.status !== "success") {
    const message = result.message?.trim() || "Failed to update Classification.";
    throw new Error(message);
  }

  invalidateAutoTaggingPlanNamesCache();
  return result;
}

/** Load one Classification plan by id (edit page hydrate / deep link). */
export async function fetchAutoTaggingPlanById(
  planId: string,
): Promise<AutoTaggingPlanRecord | null> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.get<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
  );
  if (result.status !== "success") {
    throw new Error(
      result.message?.trim() || "Failed to load Classification plan.",
    );
  }

  const payload = result.data;
  if (payload == null) return null;
  // Some hosts wrap the plan in `{ plan }` / `{ data }`.
  const raw =
    typeof payload === "object" &&
    payload !== null &&
    "id" in (payload as object)
      ? payload
      : typeof payload === "object" &&
          payload !== null &&
          "plan" in (payload as object)
        ? (payload as { plan: unknown }).plan
        : payload;
  return normalizeAutoTaggingPlanRecord(raw, 0);
}

export async function submitAssessmentPlanItems(
  items: Array<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
    conceptValues: Array<{
      code: string;
      name?: string;
      description?: string;
    }>;
    assessmentPrompt: string;
    assessmentName?: string;
    allValues?: boolean;
    active?: boolean;
    layer?: number;
    referenceItems?: AutoTaggingPlanDimensionItemWrite[];
    assessmentRecordItems?: AutoTaggingPlanDimensionItemWrite[];
    assessmentType?: string;
  }>,
): Promise<ApiResponse<unknown>> {
  const plans = items.map((item) => buildAutoTaggingPlanSubmitItem(item));

  await validateDraftItemsConceptStatsAvailability(items);

  const submitResponse = await submitAutoTaggingPlans(plans);
  if (submitResponse.status !== "success") {
    const message =
      submitResponse.message?.trim() || "Failed to submit assessment plan items.";
    throw new Error(message);
  }

  return submitResponse;
}

export interface AutoTaggingPlansExportPayload {
  file_path?: string;
  presigned_url: string;
  expires_in?: number;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function filenameFromExportPath(filePath: string | undefined): string | null {
  if (!filePath?.trim()) return null;
  const segment = filePath.split("/").filter(Boolean).pop();
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function buildAutoTaggingPlansExportFilename(filePath?: string): string {
  const dateStr = new Date().toISOString().slice(0, 10);
  const fromPath = filenameFromExportPath(filePath);
  if (fromPath) {
    const baseName = fromPath.replace(/\.[^.]+$/, "");
    return `${baseName || fromPath}.csv`;
  }
  return `auto-tagging-plan-export-${dateStr}.csv`;
}

function ensureCsvFilename(filename: string): string {
  if (/\.csv$/i.test(filename)) return filename;
  const baseName = filename.replace(/\.[^.]+$/, "");
  return `${baseName || filename}.csv`;
}

function parseFilenameFromContentDisposition(header: string): string | null {
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename=([^;\s]+)/i.exec(header);
  if (plain?.[1]) return plain[1].replace(/(?:^"|"$)/g, "");
  return null;
}

function extractAutoTaggingPlansExportPayload(
  payload: unknown,
): AutoTaggingPlansExportPayload {
  if (!payload || typeof payload !== "object") {
    throw new Error("Export failed: invalid response.");
  }

  const root = payload as Record<string, unknown>;
  let node: Record<string, unknown> = root;
  if ("data" in root && root.data && typeof root.data === "object") {
    node = root.data as Record<string, unknown>;
  }

  const presignedUrl = node.presigned_url;
  if (typeof presignedUrl !== "string" || !presignedUrl.trim()) {
    const message =
      typeof root.message === "string" && root.message.trim()
        ? root.message.trim()
        : "Export failed: download URL is missing.";
    throw new Error(message);
  }

  return {
    file_path: typeof node.file_path === "string" ? node.file_path : undefined,
    presigned_url: presignedUrl.trim(),
    expires_in:
      typeof node.expires_in === "number" ? node.expires_in : undefined,
  };
}

async function downloadExportFromPresignedUrl(
  presignedUrl: string,
  fallbackFilename: string,
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  });
  if (!response.ok) {
    let detail = "";
    try {
      detail = await response.text();
    } catch {
      /* ignore */
    }
    throw new Error(
      `Export download failed (${response.status}). ${detail.slice(0, 300)}`,
    );
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error("Export failed: empty file received.");
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  const filename = ensureCsvFilename(
    (contentDisposition
      ? parseFilenameFromContentDisposition(contentDisposition)
      : null) || fallbackFilename,
  );
  triggerBlobDownload(blob, filename);
}

export async function exportAutoTaggingPlans(): Promise<void> {
  const result = await apiClient.get<unknown>("/auto-tagging/plans/export");
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to export assessment plan.";
    throw new Error(message);
  }

  const exportPayload = extractAutoTaggingPlansExportPayload(result.data ?? result);
  const fallbackFilename = buildAutoTaggingPlansExportFilename(
    exportPayload.file_path,
  );

  await downloadExportFromPresignedUrl(
    exportPayload.presigned_url,
    fallbackFilename,
  );
}

export async function patchAutoTaggingPlan(
  planId: number,
  payload: PatchAutoTaggingPlanPayload,
): Promise<AutoTaggingPlan> {
  if (USE_AUTO_TAGGING_MOCK) {
    return mockPatchAutoTaggingPlan(planId, payload);
  }
  const result = await apiClient.patch<AutoTaggingPlan>(
    `/auto-tagging/plans/${planId}`,
    payload,
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to update auto-tagging plan.");
  }
  return result.data;
}

export async function patchAutoTaggingPlanActive(
  planId: number,
  active: boolean,
): Promise<AutoTaggingPlan> {
  if (USE_AUTO_TAGGING_MOCK) {
    return mockPatchAutoTaggingPlanActive(planId, active);
  }
  const result = await apiClient.patch<AutoTaggingPlan>(
    `/auto-tagging/plans/${planId}`,
    { active },
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to update auto-tagging plan.");
  }
  return result.data;
}

export async function fetchDefaultPrompt(concept: string): Promise<string> {
  if (USE_AUTO_TAGGING_MOCK) return mockFetchDefaultPrompt(concept);
  return fetchDefaultAutoTaggingPlanPrompt(concept);
}

export async function triggerAutoTaggingBundleTask(
  request: AutoTaggingBundleTaskRequest,
): Promise<AutoTaggingBundleTaskResponse> {
  if (USE_AUTO_TAGGING_MOCK) return mockTriggerAutoTaggingBundleTask();
  const result = await apiClient.post<AutoTaggingBundleTaskResponse>(
    "/auto-tagging/bundle-task",
    request,
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to start auto-tagging.");
  }
  return result.data;
}

export function buildConceptDisplayNameMap(
  concepts: ConfigurableConcept[],
): Map<string, string> {
  const displayNameMap = new Map<string, string>();
  concepts.forEach((concept) => {
    const displayName = concept.display_name || concept.name;
    if (concept.code?.trim()) {
      displayNameMap.set(concept.code.trim(), displayName);
    }
    if (concept.name?.trim()) {
      displayNameMap.set(concept.name.trim(), displayName);
    }
  });
  return displayNameMap;
}

export function resolveConceptDisplayName(
  concept: string,
  displayNameMap: Map<string, string>,
): string {
  return displayNameMap.get(concept) ?? concept;
}

export function enrichAutoTaggingPlans(
  plans: AutoTaggingPlan[],
  concepts: ConfigurableConcept[],
): AutoTaggingPlan[] {
  const displayNameMap = buildConceptDisplayNameMap(concepts);
  return plans.map((plan) => ({
    ...plan,
    concept_display_name: resolveConceptDisplayName(plan.concept, displayNameMap),
  }));
}

export function formatPlanValues(plan: AutoTaggingPlan): string {
  return plan.all_values ? "All values" : plan.concept_values.map((v) => v.name).join(", ");
}

export function formatPlanTypeLabel(value: string | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : "";
}

export function normalizeRegulationVersion(version: string): string {
  const trimmed = version.trim();
  return trimmed.startsWith("v") ? trimmed.slice(1).trim() : trimmed;
}
